// Razorpay Standard Checkout (https://checkout.razorpay.com/v1/checkout.js)
//
// ── Where the Key ID comes from ──────────────────────────────────────────
//   GET /api/public/payment-config, i.e. the backend's RAZORPAY_KEY_ID — the
//   same value RazorpayService uses to create the order this checkout pays for.
//
//   It used to be a build-time REACT_APP_RAZORPAY_KEY_ID as well, which meant
//   the one key lived in two places: Railway for order creation, and the
//   frontend bundle for Checkout. Flipping test to live took two changes and a
//   frontend rebuild, and the two could disagree — a live order paid for with a
//   test key. Stripe had already solved this on 30 Jul 2026
//   (PublicConfigController); Razorpay never got the same treatment.
//
//   The Key ID is public by design — Razorpay Checkout is initialised with it
//   in the browser. The Key SECRET is not here and never should be; it stays in
//   RazorpayService. It always starts with `rzp_test_` or `rzp_live_`.
//
//   This is NOT the "Merchant ID" string shown on the dashboard home — that
//   value is not passed into Checkout. Only the Key ID goes in the `key` field.
//
//   REACT_APP_RAZORPAY_KEY_ID is still honoured if set, as a local-development
//   escape hatch for working without a backend. It is deliberately NOT set in
//   any deployed environment: the point of this change is that there is one
//   source of truth, and an env var quietly overriding the backend would put
//   the second one back.
//
// ── If you see: "No appropriate payment method found" ────────────────────
//   1. Use Key ID from YOUR account (Test mode toggle matches your env).
//   2. In Dashboard → Settings → Payment methods — enable Card, UPI, Wallets,
//      Netbanking, etc. for the account linked to that key.
//   3. Wrong key (another merchant, or live key in test UI) often triggers this.
//
// Usage:
//   await openRazorpayCheckout({ amount: 90000, name: 'Previu', ... });
//   // amount = subunits in paise (₹900 → 90000)

const RAZORPAY_SCRIPT_URL = 'https://checkout.razorpay.com/v1/checkout.js';

/**
 * Injects Razorpay's Checkout script and resolves once window.Razorpay exists.
 *
 * Deleted by mistake when this file was changed to fetch the Key ID from the
 * backend, while openRazorpayCheckout's `await loadRazorpay()` call below was
 * left in place. The result was a ReferenceError on every wallet top-up:
 * "loadRazorpay is not defined". No payment could start, and the failure
 * surfaced as a Razorpay error, which reads like a gateway problem rather than
 * a missing function.
 *
 * Restored verbatim. The cached promise matters — openRazorpayCheckout can be
 * called repeatedly (top-up, lab order, appointment), and without it each call
 * appends another copy of the script tag.
 *
 * A failed load resolves FALSE rather than rejecting, and clears the cache so a
 * later attempt can retry: a patient on a flaky connection should get "could
 * not load" and a working retry, not a permanently poisoned promise.
 */
let razorpayScriptPromise = null;

export function loadRazorpay() {
  if (typeof window === 'undefined') return Promise.resolve(false);
  if (window.Razorpay) return Promise.resolve(true);
  if (razorpayScriptPromise) return razorpayScriptPromise;

  razorpayScriptPromise = new Promise((resolve) => {
    const script = document.createElement('script');
    script.src = RAZORPAY_SCRIPT_URL;
    script.async = true;
    script.onload  = () => resolve(true);
    script.onerror = () => { razorpayScriptPromise = null; resolve(false); };
    document.body.appendChild(script);
  });
  return razorpayScriptPromise;
}

const API_BASE = process.env.REACT_APP_API_URL
  || 'https://previuagenticplatform-production.up.railway.app/api';

let razorpayKeyPromise = null;

/**
 * The Key ID, from the backend, once per page load (cached in the module-level
 * promise, like stripe.js — every checkout after the first reuses it).
 *
 * A local REACT_APP_RAZORPAY_KEY_ID wins if set, so a developer can work
 * against Checkout without a backend running. Nothing deployed sets it.
 *
 * There is deliberately NO hardcoded fallback any more. The old code fell back
 * to Razorpay's public docs sample key (rzp_test_1DP5mmOlF5G5ag) when the env
 * var was missing, which is worse than failing: Checkout still renders, then
 * either reports "No appropriate payment method found" or points at an account
 * that is not Previu's — a payment problem that looks like a Razorpay problem.
 * A missing key is a configuration failure and now says so.
 */
export function getRazorpayKey() {
  const fromEnv = (process.env.REACT_APP_RAZORPAY_KEY_ID || '').trim();
  if (fromEnv) return Promise.resolve(fromEnv);

  if (razorpayKeyPromise) return razorpayKeyPromise;

  razorpayKeyPromise = fetch(`${API_BASE}/public/payment-config`)
    .then((res) => {
      if (!res.ok) throw new Error(`payment-config returned HTTP ${res.status}`);
      return res.json();
    })
    .then((data) => {
      const key = (data?.razorpayKeyId || '').trim();
      if (!key) {
        throw new Error(
          'The backend returned no Razorpay Key ID. Set RAZORPAY_KEY_ID in Railway.',
        );
      }
      return key;
    })
    .catch((err) => {
      razorpayKeyPromise = null; // don't cache a failure — allow a retry
      throw err;
    });

  return razorpayKeyPromise;
}

/**
 * Checkout session window, in seconds — DEF-035.
 *
 * 10 minutes: long enough for a UPI collect request to reach the patient's bank
 * app, be approved, and settle back (the slowest common path in India), without
 * leaving a payment window open indefinitely against an order whose amount was
 * fixed server-side at creation time.
 *
 * Passing this explicitly is the point. Razorpay Checkout shows a countdown
 * either way; previously it counted down from an unstated default, so the number
 * on screen was not one anybody here had chosen.
 *
 * Deleted alongside loadRazorpay() when this file moved to a backend-served Key
 * ID, while the reference below survived — the second of two ReferenceErrors
 * that made every top-up fail.
 */
export const CHECKOUT_TIMEOUT_SECONDS = 600;

export async function openRazorpayCheckout(options = {}) {
  const ok = await loadRazorpay();
  if (!ok) throw new Error('Failed to load Razorpay Checkout script.');

  const key = await getRazorpayKey();
  if (!key.startsWith('rzp_')) {
    throw new Error(
      'Invalid Razorpay Key ID: must start with rzp_test_ or rzp_live_. '
      + 'Check RAZORPAY_KEY_ID in Railway — it is the value Checkout is '
      + 'initialised with, and the same one RazorpayService creates orders with.',
    );
  }

  const amount = Math.round(Number(options.amount));
  if (!Number.isFinite(amount) || amount < 100) {
    throw new Error('Razorpay amount must be at least 100 paise (₹1.00) as a whole number.');
  }

  // DEF-035 — "Razorpay payment timer shows during top-up. Expired session
  // handling unclear."
  //
  // The timer the tester saw is Razorpay Checkout's own session countdown. It was
  // appearing on whatever default Razorpay chose, because `timeout` was never
  // passed — so neither the patient nor this code knew how long the window
  // actually was.
  //
  // Two things are fixed. The window is now EXPLICIT, so the countdown shown is
  // one we chose. And expiry is now DISTINGUISHABLE from the patient closing the
  // modal: Razorpay fires modal.ondismiss for both, with no reason attached, so
  // this compares elapsed time against the configured timeout. Slightly
  // indirect, and marked as such — but the alternative was reporting an expired
  // session as a deliberate cancellation, which is what made the handling
  // "unclear": the patient saw a timer run out and then nothing explaining it.
  const timeoutSeconds = Number(options.timeoutSeconds) > 0
    ? Math.round(Number(options.timeoutSeconds))
    : CHECKOUT_TIMEOUT_SECONDS;
  const openedAt = Date.now();

  return new Promise((resolve, reject) => {
    const rzp = new window.Razorpay({
      key,
      amount,
      currency: (options.currency || 'INR').toUpperCase(),
      name:     options.name        || 'Previu',
      description: options.description || 'Lab Test Payment',
      image:    options.image,
      order_id: options.orderId,
      prefill:  options.prefill  || {},
      notes:    options.notes    || {},
      theme:    { color: options.themeColor || '#1a7a3a' },
      timeout:  timeoutSeconds,
      // On a phone Razorpay's default layout leads with a single recommended
      // method (usually Google Pay's one-tap intent) and hides the rest behind
      // "More Options". Declaring the blocks explicitly lists UPI, Cards,
      // Netbanking and Wallet up front on every screen size, in this order.
      // A block whose method is not enabled on the Razorpay account is simply
      // omitted by Checkout.
      config: {
        display: {
          blocks: {
            upi:        { name: 'Pay via UPI',  instruments: [{ method: 'upi' }] },
            card:       { name: 'Cards',        instruments: [{ method: 'card' }] },
            netbanking: { name: 'Netbanking',   instruments: [{ method: 'netbanking' }] },
            wallet:     { name: 'Wallets',      instruments: [{ method: 'wallet' }] },
          },
          sequence: ['block.upi', 'block.card', 'block.netbanking', 'block.wallet'],
          preferences: { show_default_blocks: false },
        },
      },
      modal: {
        ondismiss: () => {
          // Razorpay closes the modal on expiry too, via this same callback and
          // with no reason. If we are at or past the window we configured, it
          // expired; anything sooner is the patient closing it.
          const elapsedSeconds = (Date.now() - openedAt) / 1000;
          // 2s of slack for the modal's own close animation and clock skew.
          if (elapsedSeconds >= timeoutSeconds - 2) {
            const expiredError = new Error(
              'The payment window timed out before the payment completed. '
              + 'Nothing was charged — please try again.',
            );
            expiredError.expired = true;
            reject(expiredError);
            return;
          }
          resolve(null);
        },
      },
      handler: (response) => resolve(response),
    });

    rzp.on('payment.failed', (resp) => {
      reject(new Error(resp?.error?.description || 'Payment failed.'));
    });

    rzp.open();
  });
}
